import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, type Location } from "react-router-dom";
import { useTranslation } from "react-i18next";

import clsx from "clsx";

import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoadingScreen } from "./components/LoadingScreen";
import { SessionToastAnnouncer } from "./components/SessionToastAnnouncer";
import { MobileInstallPrompt } from "./components/MobileInstallPrompt";
import { MobileNavBar } from "./components/MobileNavBar";
import { NetworkStatusBanner } from "./components/NetworkStatusBanner";
import { AuthLayout } from "./features/shared/layouts/AuthLayout";
import { DashboardPage, ProfileActivityPage, ProfileSessionsPage, ProfileSettingsPage } from "./features/user";
import { SupportConversationPage, AdminSupportInboxPage } from "./features/support";
import {
  AdminAuditLogsPage,
  AdminDashboardPage,
  AdminTiePointFormPage,
  AdminTiePointsPage,
  AdminUserFormPage,
  AdminUsersPage,
} from "./features/admin";
import {
  MyWorkspacesPage,
  WorkspaceEditPage,
  WorkspaceInvitationLandingPage,
  WorkspaceMembersPage,
  WorkspaceCreatePage,
} from "./features/workspaces";
import {
  LotDetailPage,
  LotDocumentsPage,
  LotFormPage,
  LotMovePage,
  WorkspaceLotsMapPage,
  AllLotsMapPage,
  WorkspaceLotsPage,
} from "./features/lot";
import {
  ForgotPasswordPage,
  LoginPage,
  OAuthCallbackPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "./features/auth";
import { TermsOfServicePage } from "./pages/legal/TermsOfServicePage";
import { PrivacyPolicyPage } from "./pages/legal/PrivacyPolicyPage";
import { CookiePolicyPage } from "./pages/legal/CookiePolicyPage";
import { useAuth } from "./providers/AuthProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import { useMobileNavVisibility } from "./hooks/useMobileNavVisibility";
import { useScrollRestoration } from "./hooks/useScrollRestoration";
import { BillingCheckoutStubPage, BillingPage } from "./features/billing";
import { SignupBonusPage } from "./pages/welcome/SignupBonusPage";
import { PublicLotPage } from "./features/marketplace/pages/PublicLotPage";

export default function App() {
  const { isInitializing } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const { shouldShowMobileNav } = useMobileNavVisibility();
  useScrollRestoration();
  
  if (isInitializing) {
    return <LoadingScreen message={t("app.loading")} />;
  }

  return (
    <ThemeProvider>
      <SessionActivityTracker />
      <SessionToastAnnouncer />
      <NetworkStatusBanner />
      <MobileInstallPrompt />
      <div
        className={clsx(
          "relative flex min-h-screen flex-col",
          shouldShowMobileNav ? "pb-24" : null,
        )}
      >
        <div
          key={`${location.pathname}${location.search}`}
          className="pm-route-fade flex-1"
        >
          <AppRoutes location={location} />
        </div>
        <MobileNavBar visible={shouldShowMobileNav} />
      </div>
    </ThemeProvider>
  );
}

function AppRoutes({ location }: { location: Location }) {
  return (
    <Routes location={location}>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/properties/:slugId" element={<PublicLotPage />} />
      <Route path="/properties/:locationSlug/:slugId" element={<PublicLotPage />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/welcome/credits"
        element={
          <ProtectedRoute>
            <SignupBonusPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportConversationPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces"
        element={
          <ProtectedRoute>
            <MyWorkspacesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces/new"
        element={
          <ProtectedRoute>
            <WorkspaceCreatePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tie-points"
        element={
          <ProtectedRoute>
            <AdminTiePointsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tie-points/new"
        element={
          <ProtectedRoute>
            <AdminTiePointFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tie-points/:controlPointId/edit"
        element={
          <ProtectedRoute>
            <AdminTiePointFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute>
            <AdminAuditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute>
            <AdminSupportInboxPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/new"
        element={
          <ProtectedRoute>
            <AdminUserFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:userId/edit"
        element={
          <ProtectedRoute>
            <AdminUserFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <ProfileSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/sessions"
        element={
          <ProtectedRoute>
            <ProfileSessionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/activity"
        element={
          <ProtectedRoute>
            <ProfileActivityPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots"
        element={
          <ProtectedRoute>
            <WorkspaceLotsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/members"
        element={
          <ProtectedRoute>
            <WorkspaceMembersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/edit"
        element={
          <ProtectedRoute>
            <WorkspaceEditPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots/map"
        element={
          <ProtectedRoute>
            <WorkspaceLotsMapPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/map"
        element={
          <ProtectedRoute>
            <AllLotsMapPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots/new"
        element={
          <ProtectedRoute>
            <LotFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots/:lotId/edit"
        element={
          <ProtectedRoute>
            <LotFormPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots/:lotId/move"
        element={
          <ProtectedRoute>
            <LotMovePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots/:lotId/documents"
        element={
          <ProtectedRoute>
            <LotDocumentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/workspaces/:workspaceId/lots/:lotId"
        element={
          <ProtectedRoute>
            <LotDetailPage />
          </ProtectedRoute>
        }
      />

      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      <Route path="/auth/callback" element={<OAuthCallbackPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email-change" element={<VerifyEmailPage />} />

      <Route path="/workspaces/invite" element={<WorkspaceInvitationLandingPage />} />
      <Route path="/legal/terms" element={<TermsOfServicePage />} />
      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/cookies" element={<CookiePolicyPage />} />
      <Route path="/billing/checkout/:sessionId" element={<BillingCheckoutStubPage />} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function SessionActivityTracker() {
  const location = useLocation();
  const { accessToken, resetSessionTimer } = useAuth();

  useEffect(() => {
    if (accessToken) {
      resetSessionTimer();
    }
  }, [location.pathname, location.search, accessToken, resetSessionTimer]);

  return null;
}
